import {useCallback, useMemo, useState} from 'react';

import {colors} from 'shells/colors/colors';
// @ts-expect-error - imported from .js/.jsx file
import {LoadingIcon} from 'shells/icon/svg/loading-icon';
import {DropdownSelectInline} from 'shells/select-inline/dropdown-select-inline';
import {Body} from 'shells/typography/body';

import type {FormPageRuleInput, PageRuleFragment} from 'nutshell-graphql-types';

import {
    buildNavDestinationOptions,
    defaultRuleToNavOptionId,
    navOptionToFormPageRuleInput,
} from './helpers';
import type {NavOption} from './types';

import styles from './page-default-rule-editor.module.css';

interface Props {
    pageNumber: number;
    formPageId: string | null | undefined;
    isLastPage: boolean;
    currentDefaultRule: PageRuleFragment | null | undefined;
    destinationPages: NavOption[];
    onUpdateDefaultRule: (rule: FormPageRuleInput) => Promise<void>;
}

export function PageDefaultRuleEditor(props: Props) {
    const {
        pageNumber,
        formPageId,
        isLastPage,
        currentDefaultRule,
        destinationPages,
        onUpdateDefaultRule,
    } = props;

    const [isLoading, setIsLoading] = useState<boolean>(false);

    const navOptions = useMemo(
        () =>
            buildNavDestinationOptions({
                sourceFormPageId: formPageId,
                isLastPage,
                pages: destinationPages,
            }),
        [destinationPages, formPageId, isLastPage]
    );

    const selectedOptionId = defaultRuleToNavOptionId(currentDefaultRule, isLastPage);

    const selectedOption = useMemo(() => {
        if (!selectedOptionId) {
            return null;
        }
        return navOptions.find((option) => option.id === selectedOptionId) ?? null;
    }, [navOptions, selectedOptionId]);

    const handleDestinationChange = useCallback(
        (selected: {id: string; name: string} | null) => {
            if (!formPageId || !selected) {
                return;
            }
            const ruleInput = navOptionToFormPageRuleInput(selected.id, currentDefaultRule?.id);
            setIsLoading(true);
            onUpdateDefaultRule(ruleInput).finally(() => setIsLoading(false));
        },
        [currentDefaultRule?.id, formPageId, onUpdateDefaultRule]
    );

    const isNavigationDisabled = !formPageId;

    return (
        <div className={styles['page-default-rule-editor']}>
            <div className='min-width-0' />
            <div className='flex align-center gap-8'>
                <Body size='small'>After page {pageNumber}:</Body>
                <DropdownSelectInline
                    options={navOptions}
                    selectedValue={selectedOption}
                    onChange={handleDestinationChange}
                    isDisabled={isNavigationDisabled}
                    hasSearch={false}
                    isSmall={true}
                />
            </div>
            <div className='flex align-center justify-start pl-8 min-width-0 mt-2'>
                {isLoading && <LoadingIcon size={20} fill={colors.orangeLt} />}
            </div>
        </div>
    );
}
